import { InspectionTemplateColumn } from "@/shared/models/Inspection";

export type ColumnGroup = { key: string; columns: InspectionTemplateColumn[] };

export const normalize = (value: string) => value.trim().toUpperCase();

const groupKey = (column: InspectionTemplateColumn, index: number) =>
	column.GroupName?.trim()
		? `group:${normalize(column.GroupName)}`
		: `column:${index}`;

const distance = (a: string, b: string) => {
	let previous = Array.from({ length: b.length + 1 }, (_value, i) => i);

	for (let i = 1; i <= a.length; i++) {
		const current = [i];

		for (let j = 1; j <= b.length; j++) {
			current[j] = Math.min(
				previous[j] + 1,
				current[j - 1] + 1,
				previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
			);
		}

		previous = current;
	}

	return previous[b.length];
};

export const groupNamesOf = (
	columns: InspectionTemplateColumn[],
	index: number,
) =>
	Array.from(
		new Set(
			columns
				.filter((_column, position) => position !== index)
				.map((column) => column.GroupName?.trim())
				.filter((groupName): groupName is string => !!groupName),
		),
	).sort();

export const filterGroupNames = (
	options: string[],
	state: { inputValue: string },
) => {
	const term = normalize(state.inputValue);

	if (term === "") return options;

	return options.filter((option) => {
		const candidate = normalize(option);

		return (
			candidate.includes(term) ||
			distance(candidate, term) <= Math.max(1, Math.floor(term.length / 4))
		);
	});
};

export const regroup = (columns: InspectionTemplateColumn[]) => {
	const openedAt = new Map<string, number>();

	columns.forEach((column, index) => {
		const key = groupKey(column, index);

		if (!openedAt.has(key)) openedAt.set(key, index);
	});

	return columns
		.map((column, index) => ({
			column,
			index,
			group: openedAt.get(groupKey(column, index)) ?? index,
		}))
		.sort((a, b) => a.group - b.group || a.index - b.index)
		.map((entry) => entry.column);
};

export const buildGroups = (columns: InspectionTemplateColumn[]) => {
	const groups: ColumnGroup[] = [];

	columns.forEach((column, index) => {
		const key = groupKey(column, index);
		const last = groups[groups.length - 1];

		if (column.GroupName?.trim() && last?.key === key)
			last.columns.push(column);
		else groups.push({ key, columns: [column] });
	});

	return groups;
};

export const splitGroups = (columns: InspectionTemplateColumn[]) => {
	const seen = new Map<string, number>();
	const split: string[] = [];

	columns.forEach((column, index) => {
		if (!column.GroupName?.trim()) return;

		const key = normalize(column.GroupName);
		const last = seen.get(key);

		if (last !== undefined && last !== index - 1 && !split.includes(key))
			split.push(key);

		seen.set(key, index);
	});

	return split;
};

export const settleGroupNames = (columns: InspectionTemplateColumn[]) =>
	regroup(
		columns.map((column, index) => {
			const typed = column.GroupName?.trim();

			if (!typed)
				return column.GroupName === null
					? column
					: { ...column, GroupName: null };

			const known = groupNamesOf(columns, index).find(
				(option) => normalize(option) === normalize(typed),
			);

			return known && known !== column.GroupName
				? { ...column, GroupName: known }
				: column;
		}),
	);

export const moveGroup = (groups: ColumnGroup[], from: number, to: number) => {
	const next = [...groups];
	const [moved] = next.splice(from, 1);

	next.splice(to > from ? to - 1 : to, 0, moved);

	return next.flatMap((group) => group.columns);
};
